import React, { useCallback, useEffect } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, Stack, TextField } from '@mui/material';

import AccommodationPropertyStateIndicator from '~/components/Accommodation/Pages/Properties/components/AccommodationPropertyStateIndicator';
import AccommodationPropertyDebugModal from '~/components/Accommodation/Pages/PropertyDetails/AccommodationPropertyDebugModal';
import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';
import PageHeader from '~/components/Common/Elements/PageHeader';

import { patchProperty } from '~/services/AccommodationService';

import AccommodationLinksPanel from '../Properties/components/AccommodationLinksPanel';

interface PropertyNameFormData {
  name: string;
}

export default function AccommodationPropertyDetailsOverviewPane() {
  const { propertyDetails, setPropertyDetails } = useAccommodationPropertyDetailsContext();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, watch, reset } = useForm<PropertyNameFormData>({
    defaultValues: {
      name: propertyDetails.name || '',
    },
  });

  const currentName = watch('name');
  const hasChanges = currentName !== propertyDetails.name;

  // Reset form when propertyDetails changes
  useEffect(() => {
    reset({
      name: propertyDetails.name || '',
    });
  }, [propertyDetails.name, reset]);

  const updatePropertyMutation = useMutation({
    mutationFn: async (data: PropertyNameFormData) => {
      return await patchProperty({ propertyId: propertyDetails.id }, data);
    },
    onSuccess: (response) => {
      const updatedProperty = response.result;
      setPropertyDetails(updatedProperty);
      queryClient.invalidateQueries({ queryKey: ['property', propertyDetails.id] });
      enqueueSnackbar('Property name updated successfully', { variant: 'success' });
      reset({ name: updatedProperty.name });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating property name: ${error.message}`, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    (data: PropertyNameFormData) => {
      updatePropertyMutation.mutate(data);
    },
    [updatePropertyMutation],
  );

  return (
    <Stack direction="column">
      <PageHeader title={propertyDetails.name} backButton="/accommodation/properties">
        <Stack direction="row" justifyContent="space-between">
          <Box display="flex" gap={2} alignItems="center">
            {propertyDetails.indicativePropertyState && (
              <AccommodationPropertyStateIndicator property={propertyDetails} />
            )}
            <AccommodationPropertyDebugModal />
          </Box>
        </Stack>
      </PageHeader>

      <Box display="grid" gridTemplateColumns="repeat(8, 1fr)" gap={2} justifyContent="space-between">
        <Box gridColumn="span 6">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="grid" gridTemplateColumns="3fr 1fr" gap={2}>
              <Box display="grid" gap={2} gridTemplateColumns="repeat(5, 1fr)">
                <Box gridColumn="span 2">
                  <DefinitionTitle>Name</DefinitionTitle>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={updatePropertyMutation.isPending}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <DefinitionTitle>Chain</DefinitionTitle>
                  <DefinitionText>{propertyDetails.chainName}</DefinitionText>
                </Box>
                <Box>
                  <DefinitionTitle>Category</DefinitionTitle>
                  <DefinitionText>{propertyDetails.category}</DefinitionText>
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                {hasChanges && (
                  <Button type="submit" variant="contained" color="primary" disabled={updatePropertyMutation.isPending}>
                    {updatePropertyMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Box>
        <Box gridColumn="span 2" display="flex" justifyContent="flex-end">
          <AccommodationLinksPanel property={propertyDetails} />
        </Box>
      </Box>

      <Box mt={4} display="grid" gap={2} gridTemplateColumns="repeat(8, 1fr)">
        <Box gridColumn="span 2">
          <DefinitionTitle>ID</DefinitionTitle>
          <DefinitionText>{propertyDetails.id}</DefinitionText>
        </Box>
      </Box>
    </Stack>
  );
}
